/**
 * One hand-rolled panel: pointer-draggable header, pointer-resizable corner, and
 * a full keyboard model (the legacy had none). Arrow keys move the focused
 * panel; Shift + arrow keys resize it; Enter/Space toggles a grabbed state
 * (`aria-grabbed`); collapse and fullscreen are buttons, so every pointer action
 * has a keyboard equivalent. All geometry routes through the store, which clamps
 * to min/max constraints and the canvas bounds.
 */
import { useId, useRef, useState } from "react";
import type { CSSProperties, KeyboardEvent, PointerEvent as ReactPointerEvent, ReactNode } from "react";
import { observer } from "mobx-react-lite";
import { IconButton } from "../ui/icon-button";
import { classNames } from "../ui/internal/class-names";
import { KEYBOARD_MOVE_STEP, KEYBOARD_RESIZE_STEP } from "./geometry";
import type { WorkspacePanelContent } from "./content";
import type { PanelDescriptor } from "./types";
import type { WorkspaceStore } from "./workspace-store";

interface PanelViewProps {
  readonly store: WorkspaceStore;
  readonly panel: PanelDescriptor;
  readonly content?: WorkspacePanelContent | undefined;
  readonly onRemove?: ((id: string) => void) | undefined;
}

const ARROW_DELTAS: Readonly<Record<string, readonly [number, number]>> = {
  ArrowLeft: [-1, 0],
  ArrowRight: [1, 0],
  ArrowUp: [0, -1],
  ArrowDown: [0, 1],
};

export const WorkspacePanelView = observer(function WorkspacePanelView({
  content,
  onRemove,
  panel,
  store,
}: PanelViewProps): ReactNode {
  const panelRef = useRef<HTMLElement | null>(null);
  const [grabbed, setGrabbed] = useState(false);
  const hintId = useId();

  const title = content?.title;
  const ariaLabel =
    content?.label ?? (typeof title === "string" ? title : store.text("panelRegion"));

  const style: CSSProperties = {};
  if (!panel.fullscreen) {
    style.left = `${String(panel.position.x)}px`;
    style.top = `${String(panel.position.y)}px`;
    style.width = `${String(panel.size.width)}px`;
    if (!panel.collapsed) style.height = `${String(panel.size.height)}px`;
  }

  function handleKeyDown(event: KeyboardEvent<HTMLElement>): void {
    if (event.target !== event.currentTarget) return; // child controls own their keys

    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      setGrabbed((previous) => !previous);
      return;
    }

    const delta = ARROW_DELTAS[event.key];
    if (delta === undefined) return;
    event.preventDefault();
    const step = event.shiftKey ? KEYBOARD_RESIZE_STEP : KEYBOARD_MOVE_STEP;
    if (event.shiftKey) store.resizePanelBy(panel.id, delta[0] * step, delta[1] * step);
    else store.movePanelBy(panel.id, delta[0] * step, delta[1] * step);
  }

  function startHeaderDrag(event: ReactPointerEvent<HTMLElement>): void {
    if (event.button !== 0 || panel.fullscreen) return;
    if (event.target instanceof HTMLElement && event.target.closest("button") !== null) return;
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = panel.position;
    panelRef.current?.focus();

    const move = (native: PointerEvent): void => {
      store.setPanelPosition(panel.id, {
        x: origin.x + (native.clientX - startX),
        y: origin.y + (native.clientY - startY),
      });
    };
    const up = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  function startCornerResize(event: ReactPointerEvent<HTMLElement>): void {
    if (event.button !== 0 || panel.fullscreen) return;
    event.stopPropagation();
    const startX = event.clientX;
    const startY = event.clientY;
    const origin = panel.size;

    const move = (native: PointerEvent): void => {
      store.setPanelSize(panel.id, {
        width: origin.width + (native.clientX - startX),
        height: origin.height + (native.clientY - startY),
      });
    };
    const up = (): void => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  }

  return (
    <section
      aria-describedby={hintId}
      aria-grabbed={grabbed}
      aria-label={ariaLabel}
      className={classNames(
        "thw-panel",
        panel.collapsed && "thw-panel--collapsed",
        panel.fullscreen && "thw-panel--fullscreen",
        grabbed && "thw-panel--grabbed",
      )}
      data-panel-id={panel.id}
      onKeyDown={handleKeyDown}
      ref={panelRef}
      role="group"
      style={style}
      tabIndex={0}
    >
      <header className="thw-panel__header" onPointerDown={startHeaderDrag}>
        <h3 className="thw-panel__title">{title ?? ariaLabel}</h3>
        <div className="thw-panel__actions">
          <IconButton
            icon={panel.collapsed ? "▸" : "▾"}
            label={panel.collapsed ? store.text("expand") : store.text("collapse")}
            onClick={() => {
              store.toggleCollapsed(panel.id);
            }}
            variant="ghost"
          />
          <IconButton
            icon={panel.fullscreen ? "▢" : "⤢"}
            label={panel.fullscreen ? store.text("exitFullscreen") : store.text("enterFullscreen")}
            onClick={() => {
              store.toggleFullscreen(panel.id);
            }}
            variant="ghost"
          />
          {onRemove === undefined ? null : (
            <IconButton
              className="thw-icon-button--dismiss"
              icon="✕"
              label={store.text("removePanel")}
              onClick={() => {
                onRemove(panel.id);
              }}
              variant="ghost"
            />
          )}
        </div>
      </header>
      <p className="sr-only" id={hintId}>
        {store.text("moveHint")}
      </p>
      {panel.collapsed ? null : (
        <div className="thw-panel__body">
          {content === undefined ? (
            <p className="thw-panel__empty">{store.text("emptyContent")}</p>
          ) : (
            content.render()
          )}
        </div>
      )}
      {panel.fullscreen ? null : (
        <span
          aria-hidden="true"
          className="thw-panel__resize"
          onPointerDown={startCornerResize}
        />
      )}
    </section>
  );
});
